import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { CompanyForm } from "@/components/CompanyForm";
import { Button } from "@/components/ui/button";

export default function SettingsPage() {
  const { company, companies, refresh, setCompanyId } = useCompany();
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(params.get("new") === "1" || companies.length === 0);
  const navigate = useNavigate();

  useEffect(() => {
    if (params.get("new") === "1") setCreating(true);
    else if (companies.length) setCreating(false);
  }, [params, companies.length]);

  const onSaved = async (data) => {
    await refresh();
    setCompanyId(data.id);
    setCreating(false);
    setParams({});
    if (params.get("new") === "1") navigate("/");
  };

  const remove = async () => {
    if (!window.confirm(`Supprimer « ${company.name} » ainsi que ses salariés et déclarations ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/companies/${company.id}`);
      toast.success("Entreprise supprimée");
      await refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="eyebrow">Paramètres</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground mt-1">{creating ? "Nouvelle entreprise" : company?.name || "Entreprise"}</h1>
        </div>
        <div className="flex gap-2">
          {!creating && <Button variant="outline" onClick={() => setCreating(true)} data-testid="company-add-button"><Plus className="h-4 w-4 mr-2" /> Ajouter une entreprise</Button>}
          {!creating && company && <Button variant="outline" className="text-red-600 dark:text-red-400 border-red-200 dark:border-red-500/40 hover:bg-red-50 dark:hover:bg-red-500/10" onClick={remove} data-testid="company-delete-button"><Trash2 className="h-4 w-4 mr-2" /> Supprimer</Button>}
        </div>
      </div>
      {creating ? (
        <CompanyForm key="new" company={null} onSaved={onSaved} onCancel={companies.length ? () => { setCreating(false); setParams({}); } : null} />
      ) : company ? (
        <CompanyForm key={company.id} company={company} onSaved={onSaved} />
      ) : null}
      {!creating && companies.length > 1 && (
        <div className="bg-card border border-border rounded-md p-5">
          <div className="eyebrow mb-3">Toutes les entreprises ({companies.length})</div>
          <ul className="divide-y divide-border/60">
            {companies.map((c) => (
              <li key={c.id} className="flex items-center justify-between py-2 text-sm">
                <span className={c.id === company?.id ? "font-semibold text-foreground" : "text-foreground/80"}>{c.name}{c.is_association && <span className="ml-2 text-xs text-amber-700 dark:text-amber-400">Association</span>}</span>
                <Button size="sm" variant="ghost" onClick={() => setCompanyId(c.id)} data-testid="company-select-button">Sélectionner</Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
